import React from 'react';
import styles from './MoonshotPopover.less';
import Icon, { IconName } from 'app/common/components/controls/icon';
import {
  Button,
  ButtonSizes,
} from 'app/common/components/controls/button/button';
import {
  useMoonshotPopoverQuery,
  useMoonshotUpdateCampaignMutation,
} from '@trello/graphql/generated';
import { OrganizationFeatures } from '@trello/product-features';
import RouterLink from 'app/common/components/singlespa/router-link';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { idToDate } from '@trello/dates';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { MoonshotTestIds, TestId } from '@trello/webdriverio-tests';
import { Campaigns } from 'app/common/data/campaigns';

interface PopoverListItemProps {
  iconName: IconName;
  content: string;
  href: string;
  testId: TestId;
}

const PopoverListItem: React.FC<PopoverListItemProps> = ({
  iconName,
  content,
  href,
  testId,
}) => (
  <RouterLink
    testId={testId}
    onClick={() =>
      trackUe({
        category: Noun.MOONSHOT,
        verb: Verb.OPENS,
        directObj: Noun.MOONSHOT_WELCOME_POPOVER,
        prepositionalObj: content,
      })
    }
    className={styles.list}
    href={href}
  >
    <Icon className={styles.listIcon} name={iconName} />
    {content}
  </RouterLink>
);

const byDateCreated = (id1: string, id2: string) =>
  idToDate(id1).getTime() - idToDate(id2).getTime();

interface PopoverProps {
  orgId: string;
}

export const MoonshotPopover: React.FC<PopoverProps> = ({ orgId }) => {
  const { data } = useMoonshotPopoverQuery();
  const [
    updateCampaign,
    { loading: saving },
  ] = useMoonshotUpdateCampaignMutation();

  const member = data && data.member;
  if (!member || !member.organizations[0]) {
    return null;
  }

  const campaign = member.campaigns.find((c) => c.name === Campaigns.MOONSHOT);
  if (!campaign || campaign.dateDismissed !== null) {
    return null;
  }

  const org = member.organizations.sort((a, b) => byDateCreated(a.id, b.id))[0];
  const doesOrgBelongToMoonshot = org.id === orgId;
  if (org && !doesOrgBelongToMoonshot) {
    return null;
  }

  const firstBoard = member.boards
    .filter((board) => board.idOrganization === org.id)
    .sort((a, b) => byDateCreated(a.id, b.id))[0];

  if (!saving) {
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.VIEWS,
      directObj: Noun.MOONSHOT_WELCOME_POPOVER,
    });
  }

  const numInvited = org.memberships.length - 1;
  const isPremium = OrganizationFeatures.hasProduct(org.products[0]);

  const ExplorePowerUps: PopoverListItemProps = {
    iconName: IconName.PowerUp,
    content: 'Explore Power-Ups',
    href: '/power-ups',
    testId: MoonshotTestIds.WelcomePopoverExplorePowerUps,
  };
  const TeamLink: PopoverListItemProps = isPremium
    ? {
        iconName: IconName.Gear,
        content: 'Explore Team Settings',
        href: `/${org.name}/account`,
        testId: MoonshotTestIds.WelcomePopoverExploreTeamSettings,
      }
    : {
        iconName: IconName.BusinessClass,
        content: 'Explore Business Class',
        href: `/${org.name}/billing`,
        testId: MoonshotTestIds.WelcomePopoverExploreBC,
      };

  let ctaLinks = [ExplorePowerUps, TeamLink];

  if (firstBoard) {
    const ExploreBoards: PopoverListItemProps = {
      iconName: IconName.Board,
      content: 'Explore My Boards',
      href: `/b/${firstBoard.shortLink}`,
      testId: MoonshotTestIds.WelcomePopoverExploreBoards,
    };

    ctaLinks = [ExploreBoards, ...ctaLinks];
  }

  const onDismiss = async () => {
    if (saving) {
      return;
    }

    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.DISMISSES,
      directObj: Noun.MOONSHOT_WELCOME_POPOVER,
    });

    await updateCampaign({
      variables: {
        id: campaign.id,
        isDismissed: true,
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.popover}>
        <h1>
          Welcome to Trello!{' '}
          <span role="img" aria-label="waving hand">
            👋
          </span>
        </h1>
        {numInvited > 0 ? (
          <p className={styles.invite}>
            You invited<span>&nbsp;</span>
            <strong>
              {numInvited} {numInvited === 1 ? 'other' : 'others'}
            </strong>{' '}
            to this team. We'll let you know when they accept the invite.{' '}
            <RouterLink
              testId={MoonshotTestIds.WelcomePopoverInvite}
              onClick={() =>
                trackUe({
                  category: Noun.MOONSHOT,
                  verb: Verb.OPENS,
                  directObj: Noun.MOONSHOT_WELCOME_POPOVER,
                  prepositionalObj: 'Add Additional Members',
                })
              }
              href={`/${org.name}/members`}
            >
              Invite more team members.
            </RouterLink>
          </p>
        ) : (
          <p className={styles.invite}>
            You're the only one on this team.{' '}
            <RouterLink
              testId={MoonshotTestIds.WelcomePopoverInvite}
              onClick={() =>
                trackUe({
                  category: Noun.MOONSHOT,
                  verb: Verb.OPENS,
                  directObj: Noun.MOONSHOT_WELCOME_POPOVER,
                  prepositionalObj: 'Add Additional Members',
                })
              }
              href={`/${org.name}/members`}
            >
              Do you want to invite some team members?
            </RouterLink>
          </p>
        )}
        <p className={styles.content}>
          Trello can be used for every kind of project imaginable. It is perfect
          for organizing, tracking, storing, or sharing.{' '}
          <strong>Here are a few ideas for how to get started:</strong>
        </p>
        {ctaLinks.map((props, i) => (
          <PopoverListItem key={i} {...props} />
        ))}
        <Button
          testId={MoonshotTestIds.WelcomePopoverDismiss}
          className={styles.button}
          size={ButtonSizes.FULLWIDTH}
          onClick={onDismiss}
          disabled={saving}
        >
          Got it! Dismiss this.
        </Button>
      </div>
      <div className={styles.arrowWrapper}>
        <div className={styles.arrow} />
      </div>
      <div className={styles.iconWrapper}>
        <img
          src={require('resources/images/taco.png')}
          alt=""
          role="presentation"
        />
      </div>
    </div>
  );
};

export const MoonshotPopoverConnected: React.FC<PopoverProps> = (props) => (
  <ComponentWrapper>
    <MoonshotPopover {...props} />
  </ComponentWrapper>
);
